import React, { useState, useRef } from 'react';
import { useEditor } from '@smartfire/js-block-engine';
import { __ } from '../../../tools';
import { Button } from '../../../../button/block';
import CloseIcon from '../../../../assets/icons/close';
import clsx from 'clsx';
import style from './style.module.scss';

const File = ({ id, required, name, errors, setErrors, fileTypes, fileSize, label, button, placeholder, multiple }) => {
    const { isEditing } = useEditor();

    const acceptFileTypes = fileTypes?.replaceAll(' ', '').split(',');
    const acceptFileSize = fileSize ? fileSize * 1024 * 1024 : 2097152;

    const inputFile = useRef(null);
    const [inputFileOver, setInputFileOver] = useState(false);
    const [inputFileNames, setInputFileNames] = useState([]);

    function dropFiles(files, input, setNameCallback, setErrorCallback) {
        if (files) {
            const dT = new DataTransfer();

            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                if (file.size > acceptFileSize) {
                    setErrorCallback(__('Taille maximale :') + ' ' + acceptFileSize / 1024 / 1024 + __('Mo'));
                } else {
                    const regex = /^.+(\..+)$/g;
                    const ext = [...file.name.matchAll(regex)];
                    if (ext.length > 0 && ext[0].length !== 2) {
                        setErrorCallback(__("Impossible de récupérer l'extension du fichier"));
                    } else {
                        if (!acceptFileTypes?.includes(ext[0][1])) {
                            setErrorCallback(__('Formats acceptés :') + ' ' + acceptFileTypes?.join(', '));
                        } else {
                            setNameCallback((prevState) => [...prevState, file.name]);

                            dT.items.add(file);
                            input.current.files = dT.files;
                            setErrorCallback('');
                        }
                    }
                }
            }
        } else {
            setErrorCallback(__("Erreur lors de l'importation du fichier"));
        }
    }

    function dropOnField(ev) {
        ev.preventDefault();
        const files = [];

        if (ev.dataTransfer.items) {
            // Use DataTransferItemList interface to access the files
            for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                if (ev.dataTransfer.items[i].kind === 'file') {
                    files.push(ev.dataTransfer.items[i].getAsFile());
                }
            }
        } else {
            // Use DataTransfer interface to access the files
            for (let i = 0; i < ev.dataTransfer.files.length; i++) {
                files.push(ev.dataTransfer.files[i]);
            }
        }

        setInputFileNames([]);
        dropFiles(files, inputFile, setInputFileNames, setFileError);
        setInputFileOver(false);
    }

    function changeFile() {
        setInputFileNames([]);
        dropFiles(inputFile.current.files, inputFile, setInputFileNames, setFileError);
    }

    function dragOverFile(e) {
        e.preventDefault();
        setInputFileOver(true);
    }

    function dragLeaveFile(e) {
        e.preventDefault();
        setInputFileOver(false);
    }

    function deleteFile(e, index) {
        e && e.stopPropagation();
        setFileError('');

        setInputFileNames((prevState) => prevState.filter((_, i) => i !== index));

        const dataTransfer = new DataTransfer();

        for (let i = 0; i < inputFile.current.files.length; i++) {
            const file = inputFile.current.files[i];

            if (i !== index) {
                dataTransfer.items.add(file);
            }
        }

        inputFile.current.files = dataTransfer.files;
    }

    function setFileError(message) {
        var newErrors = errors.filter((el) => el.field !== name);
        if (message.length > 0) {
            newErrors.push({ field: name, message: message });
            setErrors(newErrors);
        } else {
            setErrors(newErrors);
        }
    }

    const err = errors.filter((el) => el.field === name);

    const dropZoneProps = isEditing
        ? {}
        : {
              onDrop: dropOnField,
              onDragOver: dragOverFile,
              onDragLeave: dragLeaveFile,
          };

    return (
        <div className={style.block}>
            <label className={clsx(style.label, required && style.required)} htmlFor={id}>
                {label}
            </label>
            <div className={clsx(style.dropZone, inputFileOver && style.active)} {...dropZoneProps}>
                {inputFileNames && inputFileNames.length > 0 ? (
                    <>
                        {inputFileNames.map((fileName, index) => (
                            <p className={style.filename} key={index}>
                                {fileName}
                                <CloseIcon onClick={(e) => deleteFile(e, index)} />
                            </p>
                        ))}
                    </>
                ) : (
                    <>
                        <Button
                            tagName="button"
                            text={button}
                            className={style.button}
                            onClick={() => !isEditing && inputFile.current.click()}
                            type="filled"
                        />
                        <p className={style.placeholder}>{placeholder}</p>
                    </>
                )}
                <input
                    className={style.input}
                    ref={inputFile}
                    type="file"
                    name={multiple ? `${name}[]` : name}
                    id={id}
                    accept={acceptFileTypes?.join(',')}
                    onChange={changeFile}
                    required={required ?? false}
                    multiple={multiple}
                />
            </div>
            {err && err.length ? <div className={style.error}>{err[0].message}</div> : <></>}
        </div>
    );
};

export default File;
